import { createClient, processLock } from '@supabase/supabase-js';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

import { isExpoWebViewHost } from '@/lib/embedded-native-shell';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    'Missing Supabase environment variables. Expected VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.',
  );
}

const fallbackWebStorage = new Map<string, string>();

function getWebStorage():
  | Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  | {
      getItem: (key: string) => string | null;
      setItem: (key: string, value: string) => void;
      removeItem: (key: string) => void;
    } {
  const storage = globalThis.localStorage as
    | Partial<Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>>
    | undefined;

  if (
    storage &&
    typeof storage.getItem === 'function' &&
    typeof storage.setItem === 'function' &&
    typeof storage.removeItem === 'function'
  ) {
    return storage as Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  }

  return {
    getItem: (key: string) => fallbackWebStorage.get(key) ?? null,
    setItem: (key: string, value: string) => {
      fallbackWebStorage.set(key, value);
    },
    removeItem: (key: string) => {
      fallbackWebStorage.delete(key);
    },
  };
}

const WebStorageAdapter = {
  getItem: (key: string): string | null => getWebStorage().getItem(key),
  setItem: (key: string, value: string): void => {
    getWebStorage().setItem(key, value);
  },
  removeItem: (key: string): void => {
    getWebStorage().removeItem(key);
  },
};

const NativeStorageAdapter = {
  getItem: async (key: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key });
    return value;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await Preferences.set({ key, value });
  },
  removeItem: async (key: string): Promise<void> => {
    await Preferences.remove({ key });
  },
};

// Use synchronous localStorage on web. Keeping the web path synchronous avoids
// queuing every PostgREST request behind async auth storage work.
const authStorageAdapter = Capacitor.isNativePlatform() ? NativeStorageAdapter : WebStorageAdapter;
// One persistent Expo WebView is a single-process runtime. Its navigator lock
// can stall Supabase initialization; processLock keeps auth serialized without
// changing the multi-tab browser path.
const expoWebViewAuthOptions = isExpoWebViewHost() ? { lock: processLock } : {};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: authStorageAdapter,
    flowType: 'pkce', // 📱 Explicit PKCE for native app session exchange
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false, // ⚠️ CRITICAL for Capacitor to prevent deeplink loops
    ...expoWebViewAuthOptions,
  },
});
